import { useState, useRef, useEffect, Fragment } from 'react';
import { marked } from 'marked';
import {
  makeStyles,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Box,
  Chip,
} from '@material-ui/core';
import {
  ExpandLess,
  ExpandMore,
  FolderOpen,
  Folder,
  Description,
} from '@material-ui/icons';

import { documentationStructure } from './documentationData';
import GlobalSearch from './GlobalSearch';
import { hasDocumentChanges, getDocumentChanges, changeColors } from './changesData';
import ChangesToolbar from './ChangesToolbar';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100vh',
    overflow: 'hidden',
    width: '100%',
    backgroundColor: '#fafafa',
  },
  header: {
    padding: '24px 32px',
    borderBottom: 'none',
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)',
    boxShadow: '0 4px 20px rgba(15, 23, 42, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '1px',
      background: 'linear-gradient(90deg, transparent 0%, rgba(148, 163, 184, 0.3) 50%, transparent 100%)',
    },
  },
  title: {
    fontWeight: 600,
    fontSize: '28px',
    color: '#ffffff',
    // letterSpacing: '-0.02em',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderRadius: '8px',
      border: '1px solid rgba(148, 163, 184, 0.3)',
      backdropFilter: 'blur(10px)',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: '#ffffff',
        borderColor: 'rgba(148, 163, 184, 0.5)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff',
        borderColor: '#3b82f6',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1), 0 4px 12px rgba(0, 0, 0, 0.15)',
      },
    },
    '& input': {
      color: '#1f2937',
      '&::placeholder': {
        color: '#6b7280',
      },
    },
  },
  content: {
    height: 'calc(100vh - 100px)',
    overflow: 'hidden',
  },
  sidebar: {
    borderRight: '1px solid #e5e7eb',
    height: '100%',
    overflow: 'auto',
    background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
    width: 300,
    padding: '16px 0',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: 'linear-gradient(90deg, #334155 0%, #475569 50%, #64748b 100%)',
    },
  },
  mainContent: {
    height: '100%',
    width: '100%',
    overflow: 'auto',
    padding: '32px',
    flex: 1,
    backgroundColor: '#fafafa',
  },
  navigationTree: {
    padding: '0 16px',
  },
  treeItem: {
    borderRadius: '6px',
    margin: '1px 0',
    minHeight: '32px',
    paddingTop: '6px',
    paddingBottom: '6px',
    position: 'relative',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: '#ffffff',
      transform: 'translateX(2px)',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    },
    '& .MuiListItemIcon-root': {
      minWidth: '32px',
      color: '#6b7280',
    },
    '& .MuiListItemText-primary': {
      fontSize: '13px',
      fontWeight: 500,
      color: '#374151',
    },
  },
  nestedItem: {
    paddingLeft: '24px',
    borderRadius: '6px',
    margin: '1px 0',
    minHeight: '28px',
    paddingTop: '4px',
    paddingBottom: '4px',
    position: 'relative',
    '&:hover': {
      backgroundColor: '#ffffff',
      transform: 'translateX(2px)',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    },
    '& .MuiListItemIcon-root': {
      minWidth: '28px',
      color: '#9ca3af',
    },
    '& .MuiListItemText-primary': {
      fontSize: '12px',
      fontWeight: 500,
      color: '#6b7280',
    },
  },
  doubleNestedItem: {
    paddingLeft: '40px',
    borderRadius: '6px',
    margin: '1px 0',
    minHeight: '26px',
    paddingTop: '3px',
    paddingBottom: '3px',
    '&:hover': {
      backgroundColor: '#ffffff',
      transform: 'translateX(2px)',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
    },
    '& .MuiListItemIcon-root': {
      minWidth: '24px',
      color: '#9ca3af',
    },
    '& .MuiListItemText-primary': {
      fontSize: '11px',
      fontWeight: 500,
      color: '#9ca3af',
    },
  },
  activeItem: {
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    color: '#ffffff',
    boxShadow: '0 4px 12px rgba(30, 41, 59, 0.25), 0 2px 4px rgba(0, 0, 0, 0.1)',
    transform: 'translateX(4px)',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    '&:hover': {
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      transform: 'translateX(4px)',
      boxShadow: '0 6px 16px rgba(30, 41, 59, 0.4), 0 2px 8px rgba(0, 0, 0, 0.15)',
    },
    '& .MuiListItemIcon-root': {
      color: '#e2e8f0',
    },
    '& .MuiListItemText-primary': {
      color: '#ffffff',
      fontWeight: 600,
      textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
    },
  },
  activeParent: {
    background: 'linear-gradient(135deg, #e2e8f0 0%, #f8fafc 100%)',
    color: '#1e293b',
    boxShadow: '0 2px 6px rgba(30, 41, 59, 0.15), 0 1px 2px rgba(0, 0, 0, 0.05)',
    transform: 'translateX(2px)',
    border: '1px solid rgba(148, 163, 184, 0.3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #cbd5e1 0%, #e2e8f0 100%)',
      transform: 'translateX(2px)',
      boxShadow: '0 4px 8px rgba(30, 41, 59, 0.2), 0 2px 4px rgba(0, 0, 0, 0.1)',
    },
    '& .MuiListItemIcon-root': {
      color: '#475569',
    },
    '& .MuiListItemText-primary': {
      color: '#1e293b',
      fontWeight: 600,
    },
  },
  disabledItem: {
    opacity: 0.4,
    cursor: 'not-allowed',
    '&:hover': {
      backgroundColor: 'transparent',
      transform: 'none',
      boxShadow: 'none',
    },
    '& .MuiListItemIcon-root': {
      color: '#d1d5db',
    },
    '& .MuiListItemText-primary': {
      color: '#d1d5db',
    },
  },
  sectionDivider: {
    height: '1px',
    backgroundColor: '#e5e7eb',
    margin: '8px 16px',
  },
  sectionLabel: {
    padding: '8px 16px',
    fontSize: '11px',
    fontWeight: 600,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginTop: '16px',
    '&:first-child': {
      marginTop: '8px',
    },
  },
  contentSection: {
    marginBottom: '32px',
  },
  sectionTitle: {
    marginBottom: '16px',
    color: '#111827',
    fontWeight: 600,
    fontSize: '32px',
    letterSpacing: '-0.025em',
  },
  sectionDescription: {
    color: '#6b7280',
    fontSize: '16px',
    lineHeight: 1.6,
    marginBottom: '24px',
  },
  breadcrumbs: {
    marginBottom: '16px',
    '& .MuiTypography-root': {
      color: '#9ca3af',
      fontSize: '14px',
    },
  },
  tag: {
    margin: '4px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
    '&:hover': {
      backgroundColor: '#e5e7eb',
    },
  },
  markdownContent: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '32px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: 'linear-gradient(90deg, #334155 0%, #475569 50%, #64748b 100%)',
      borderRadius: '12px 12px 0 0',
    },
    '& h1': {
      fontSize: '28px',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '16px',
      marginTop: '32px',
      letterSpacing: '-0.02em',
      '&:first-child': {
        marginTop: 0,
      },
    },
    '& h2': {
      fontSize: '24px',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '16px',
      marginTop: '32px',
      letterSpacing: '-0.015em',
      paddingBottom: '8px',
      borderBottom: '2px solid #e5e7eb',
    },
    '& h3': {
      fontSize: '20px',
      fontWeight: 600,
      color: '#374151',
      marginBottom: '12px',
      marginTop: '24px',
      letterSpacing: '-0.01em',
    },
    '& p': {
      marginBottom: '16px',
      lineHeight: 1.6,
      color: '#4b5563',
    },
    '& ul, & ol': {
      marginBottom: '16px',
      paddingLeft: '24px',
      color: '#4b5563',
    },
    '& li': {
      marginBottom: '8px',
      lineHeight: 1.6,
    },
    '& code': {
      backgroundColor: '#f3f4f6',
      color: '#374151',
      padding: '2px 6px',
      borderRadius: '4px',
      fontFamily: 'ui-monospace, SFMono-Regular, monospace',
      fontSize: '14px',
      border: '1px solid #e5e7eb',
    },
    '& pre': {
      backgroundColor: '#f8fafc',
      padding: '20px',
      borderRadius: '8px',
      overflow: 'auto',
      marginBottom: '16px',
      border: '1px solid #e2e8f0',
    },
    '& pre code': {
      backgroundColor: 'transparent',
      padding: 0,
      border: 'none',
    },
    '& strong': {
      fontWeight: 600,
      color: '#111827',
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      marginBottom: '24px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      overflow: 'hidden',
    },
    '& th': {
      textAlign: 'left',
      padding: '12px 16px',
      borderBottom: '2px solid #e5e7eb',
      fontWeight: 600,
      backgroundColor: '#f9fafb',
      color: '#374151',
      fontSize: '14px',
    },
    '& td': {
      textAlign: 'left',
      padding: '12px 16px',
      borderBottom: '1px solid #f3f4f6',
      color: '#4b5563',
      fontSize: '14px',
    },
    '& td:contains("✓")': {
      color: theme.palette.success.main,
    },
    '& .checkmark': {
      color: theme.palette.success.main,
    },
    '& .crossmark': {
      color: theme.palette.error.main,
    },
    '& .nested-field': {
      paddingLeft: theme.spacing(3),
      position: 'relative',
      '&:before': {
        content: '""',
        position: 'absolute',
        left: theme.spacing(1),
        top: '50%',
        width: theme.spacing(1.5),
        height: '1px',
        backgroundColor: theme.palette.grey[400],
      },
    },
    // Table row highlighting for added/modified content
    '& table tr.row-added': {
      backgroundColor: 'rgba(5, 150, 105, 0.08)',
      borderLeft: '4px solid #059669',
      transition: 'all 0.3s ease',
      '& td': {
        backgroundColor: 'rgba(5, 150, 105, 0.08)',
        borderColor: 'rgba(5, 150, 105, 0.2)',
      },
      '&.active': {
        backgroundColor: 'rgba(5, 150, 105, 0.2)',
        borderLeft: '6px solid #059669',
        transform: 'translateX(4px)',
        '& td': {
          backgroundColor: 'rgba(5, 150, 105, 0.2)',
        },
      },
    },
    '& table tr.row-modified': {
      backgroundColor: 'rgba(217, 119, 6, 0.08)',
      borderLeft: '4px solid #D97706',
      transition: 'all 0.3s ease',
      '& td': {
        backgroundColor: 'rgba(217, 119, 6, 0.08)',
        borderColor: 'rgba(217, 119, 6, 0.2)',
      },
      '&.active': {
        backgroundColor: 'rgba(217, 119, 6, 0.2)',
        borderLeft: '6px solid #D97706',
        transform: 'translateX(4px)',
        '& td': {
          backgroundColor: 'rgba(217, 119, 6, 0.2)',
        },
      },
    },
    // Change highlight styles for dynamic content
    '& .change-highlight': {
      padding: '8px 12px',
      margin: '8px 0',
      borderRadius: '8px',
      borderLeft: '4px solid',
      position: 'relative',
      transition: 'all 0.3s ease',
      '&.added': {
        backgroundColor: 'rgba(5, 150, 105, 0.15)',
        borderLeftColor: '#059669',
        border: '1px solid rgba(5, 150, 105, 0.4)',
      },
      '&.modified': {
        backgroundColor: 'rgba(217, 119, 6, 0.15)',
        borderLeftColor: '#D97706',
        border: '1px solid rgba(217, 119, 6, 0.4)',
      },
      '&.deleted': {
        backgroundColor: 'rgba(220, 38, 38, 0.15)',
        borderLeftColor: '#DC2626',
        border: '1px solid rgba(220, 38, 38, 0.4)',
        textDecoration: 'line-through',
        opacity: 0.7,
      },
      '&.new-section': {
        backgroundColor: 'rgba(37, 99, 235, 0.15)',
        borderLeftColor: '#2563EB',
        border: '1px solid rgba(37, 99, 235, 0.4)',
      },
      '&.hidden': {
        backgroundColor: 'transparent !important',
        border: '1px solid transparent !important',
        borderLeft: '4px solid transparent !important',
        opacity: '1 !important',
        textDecoration: 'none !important',
      },
      '&.active': {
        transform: 'scale(1.02)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: 10,
      }
    },
    '& .change-tooltip': {
      position: 'absolute',
      top: '-35px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: '#2c3e50',
      color: 'white',
      padding: '6px 10px',
      borderRadius: '4px',
      fontSize: '0.8em',
      whiteSpace: 'nowrap',
      opacity: 0,
      pointerEvents: 'none',
      transition: 'opacity 0.2s ease',
      zIndex: 20,
      '&::after': {
        content: '""',
        position: 'absolute',
        top: '100%',
        left: '50%',
        marginLeft: '-5px',
        width: 0,
        height: 0,
        borderTop: '5px solid #2c3e50',
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent'
      }
    },
  },
  changeIndicator: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: '#D97706', // Enhanced Amber-600
    zIndex: 1,
    opacity: 0.9,
    transition: 'all 0.2s ease',
    '&.pulse': {
      animation: '$pulse 2s infinite',
    },
    '&.parent-changes': {
      backgroundColor: '#059669', // Green for parent with child changes
      opacity: 0.7,
      '&.pulse': {
        animation: '$pulseGreen 2s infinite',
      },
    },
  },
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(217, 119, 6, 0.5)',
    },
    '70%': {
      boxShadow: '0 0 0 8px rgba(217, 119, 6, 0)',
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(217, 119, 6, 0)',
    },
  },
  '@keyframes pulseGreen': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(5, 150, 105, 0.4)',
    },
    '70%': {
      boxShadow: '0 0 0 6px rgba(5, 150, 105, 0)',
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(5, 150, 105, 0)',
    },
  },
}));

const Documentation = () => {
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState('');

  // Load expanded items from localStorage, default to empty array
  const [expandedItems, setExpandedItems] = useState(() => {
    const savedExpanded = localStorage.getItem('qhs-docs-expanded-items');
    return savedExpanded ? JSON.parse(savedExpanded) : [];
  });

  const [activeItem, setActiveItem] = useState('overview');
  const [highlightTerm, setHighlightTerm] = useState('');
  const [changesVisible, setChangesVisible] = useState(true);
  const [currentChangeIndex, setCurrentChangeIndex] = useState(0);
  const [pendingScrollToChange, setPendingScrollToChange] = useState(null);
  const contentRef = useRef();

  // Scroll to top on component mount
  useEffect(() => {
    const mainContentElement = document.querySelector(`.${classes.mainContent}`);
    if (mainContentElement) {
      mainContentElement.scrollTo({
        top: 0,
        behavior: 'auto'
      });
    }
  }, [classes.mainContent]);

  // Add hover effects for change highlights
  useEffect(() => {
    const handleMouseEnter = (e) => {
      const tooltip = e.target.querySelector('.change-tooltip');
      if (tooltip) {
        tooltip.style.opacity = '1';
      }
    };

    const handleMouseLeave = (e) => {
      const tooltip = e.target.querySelector('.change-tooltip');
      if (tooltip) {
        tooltip.style.opacity = '0';
      }
    };

    // Add event listeners to change highlights
    const addHighlightListeners = () => {
      const highlights = document.querySelectorAll('.change-highlight');
      highlights.forEach(highlight => {
        highlight.addEventListener('mouseenter', handleMouseEnter);
        highlight.addEventListener('mouseleave', handleMouseLeave);
      });
    };

    // Initial setup
    addHighlightListeners();

    // Re-add listeners when content changes
    const observer = new MutationObserver(() => {
      addHighlightListeners();
    });

    if (contentRef.current) {
      observer.observe(contentRef.current, { childList: true, subtree: true });
    }

    return () => {
      observer.disconnect();
      // Cleanup listeners
      const highlights = document.querySelectorAll('.change-highlight');
      highlights.forEach(highlight => {
        highlight.removeEventListener('mouseenter', handleMouseEnter);
        highlight.removeEventListener('mouseleave', handleMouseLeave);
      });
    };
  }, [activeItem, changesVisible]);

  // Reset change index when active item changes
  useEffect(() => {
    setCurrentChangeIndex(0);
  }, [activeItem]);

  // Handle pending scroll to change after content loads
  useEffect(() => {
    if (pendingScrollToChange !== null && contentRef.current) {
      const checkForHighlights = () => {
        const highlights = document.querySelectorAll('.change-highlight');
        const addedElements = document.querySelectorAll('table .added');
        const totalElements = highlights.length + addedElements.length;

        console.log(`🔍 Checking for highlights: found ${highlights.length} change-highlights and ${addedElements.length} .added elements (total: ${totalElements})`);

        if (totalElements > 0) {
          console.log(`🎯 Executing pending scroll to change ${pendingScrollToChange}`);
          scrollToChange(pendingScrollToChange);
          setPendingScrollToChange(null);
          return true;
        }
        return false;
      };

      // Try immediately
      if (!checkForHighlights()) {
        // If not found, try with intervals
        let attempts = 0;
        const maxAttempts = 20;
        const interval = setInterval(() => {
          attempts++;
          console.log(`🔄 Scroll attempt ${attempts}/${maxAttempts}`);

          if (checkForHighlights() || attempts >= maxAttempts) {
            clearInterval(interval);
            if (attempts >= maxAttempts) {
              console.log('❌ Max scroll attempts reached, giving up');
              setPendingScrollToChange(null);
            }
          }
        }, 200);

        // Cleanup
        return () => {
          clearInterval(interval);
        };
      }
    }
  }, [pendingScrollToChange, activeItem, changesVisible]);

  // Apply styles to dynamically created highlights
  useEffect(() => {
    const applyHighlightStyles = () => {
      const highlights = document.querySelectorAll('.change-highlight');
      highlights.forEach(highlight => {
        // Apply base styles
        highlight.style.cssText = `
          padding: 8px 12px;
          margin: 8px 0;
          border-radius: 8px;
          border-left: 4px solid;
          position: relative;
          transition: all 0.3s ease;
          display: block;
        `;

        // Apply type-specific styles
        if (highlight.classList.contains('added')) {
          highlight.style.backgroundColor = 'rgba(5, 150, 105, 0.15)';
          highlight.style.borderLeftColor = '#059669';
          highlight.style.border = '1px solid rgba(5, 150, 105, 0.4)';
        } else if (highlight.classList.contains('modified')) {
          highlight.style.backgroundColor = 'rgba(217, 119, 6, 0.15)';
          highlight.style.borderLeftColor = '#D97706';
          highlight.style.border = '1px solid rgba(217, 119, 6, 0.4)';
        } else if (highlight.classList.contains('deleted')) {
          highlight.style.backgroundColor = 'rgba(220, 38, 38, 0.15)';
          highlight.style.borderLeftColor = '#DC2626';
          highlight.style.border = '1px solid rgba(220, 38, 38, 0.4)';
          highlight.style.textDecoration = 'line-through';
          highlight.style.opacity = '0.7';
        } else if (highlight.classList.contains('new-section')) {
          highlight.style.backgroundColor = 'rgba(37, 99, 235, 0.15)';
          highlight.style.borderLeftColor = '#2563EB';
          highlight.style.border = '1px solid rgba(37, 99, 235, 0.4)';
        }

        // Apply tooltip styles
        const tooltip = highlight.querySelector('.change-tooltip');
        if (tooltip) {
          tooltip.style.cssText = `
            position: absolute;
            top: -35px;
            left: 50%;
            transform: translateX(-50%);
            background: #2c3e50;
            color: white;
            padding: 6px 10px;
            border-radius: 4px;
            font-size: 0.8em;
            white-space: nowrap;
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.2s ease;
            z-index: 20;
          `;
        }
      });

      // Apply table row highlighting for elements with .added or .modified classes
      const applyTableRowHighlighting = () => {
        // Find all table rows that contain elements with .added class
        const addedElements = document.querySelectorAll('table .added, table .change-highlight.added');
        addedElements.forEach(element => {
          const tableRow = element.closest('tr');
          if (tableRow && !tableRow.classList.contains('row-added')) {
            tableRow.classList.add('row-added');
          }
        });

        // Find all table rows that contain elements with .modified class
        const modifiedElements = document.querySelectorAll('table .modified, table .change-highlight.modified');
        modifiedElements.forEach(element => {
          const tableRow = element.closest('tr');
          if (tableRow && !tableRow.classList.contains('row-modified')) {
            tableRow.classList.add('row-modified');
          }
        });
      };

      applyTableRowHighlighting();
    };

    // Apply styles after content loads
    const timer = setTimeout(applyHighlightStyles, 100);

    // Also observe for new content
    const observer = new MutationObserver(applyHighlightStyles);
    if (contentRef.current) {
      observer.observe(contentRef.current, { childList: true, subtree: true });
    }

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [activeItem, changesVisible]);

  const handleExpandClick = (itemId) => {
    setExpandedItems(prev => {
      const newExpanded = prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId];

      // Save to localStorage
      localStorage.setItem('qhs-docs-expanded-items', JSON.stringify(newExpanded));

      return newExpanded;
    });
  };

  const handleItemClick = (itemId, changeId = null) => {
    setActiveItem(itemId);
    setHighlightTerm(''); // Clear any search highlighting

    // If we have a changeId, find its index and set it as current
    if (changeId) {
      const documentChanges = getDocumentChanges(itemId);
      const changeIndex = documentChanges.findIndex(change => change.id === changeId);
      if (changeIndex !== -1) {
        setCurrentChangeIndex(changeIndex);
        setPendingScrollToChange(changeIndex);
        console.log(`🚀 Setting up navigation to change ${changeId} at index ${changeIndex}`);
      } else {
        setCurrentChangeIndex(0);
        setPendingScrollToChange(null);
      }
    } else {
      setCurrentChangeIndex(0); // Reset change navigation
      setPendingScrollToChange(null);

      // Scroll to top of main content area instantly
      const mainContentElement = document.querySelector(`.${classes.mainContent}`);
      if (mainContentElement) {
        mainContentElement.scrollTo({
          top: 0,
          behavior: 'auto' // No animation for instant scroll
        });
      }
    }
  };

  // Handle navigation from global search
  const handleSearchNavigation = (itemId, searchTerm = '', targetSnippet = '') => {
    // If itemId is null, it means clear highlighting only
    if (itemId === null) {
      setHighlightTerm('');
      // Clear existing highlights
      if (contentRef.current) {
        const existingHighlights = contentRef.current.querySelectorAll('.search-highlight');
        existingHighlights.forEach(el => {
          const parent = el.parentNode;
          parent.replaceChild(document.createTextNode(el.textContent), el);
          parent.normalize();
        });
      }
      return;
    }

    setActiveItem(itemId);
    setHighlightTerm(searchTerm);

    // Expand parent items if needed
    const findAndExpand = (items, targetId, parentPath = []) => {
      for (const item of items) {
        if (item.id === targetId) {
          setExpandedItems(prev => [...new Set([...prev, ...parentPath])]);
          return true;
        }
        if (item.children) {
          if (findAndExpand(item.children, targetId, [...parentPath, item.id])) {
            return true;
          }
        }
      }
      return false;
    };

    findAndExpand(documentationStructure, itemId);

    // Use requestAnimationFrame for better performance and immediate execution
    const handleHighlighting = () => {
      if (searchTerm && contentRef.current) {
        // Check if content is loaded by looking for actual content
        const hasContent = contentRef.current.textContent.trim().length > 0;

        if (hasContent) {
          highlightTextInContent(searchTerm, targetSnippet);
        } else {
          // Content not ready yet, try again soon
          setTimeout(handleHighlighting, 50);
        }
      } else {
        // If no search term, just scroll to top
        const mainContentElement = document.querySelector(`.${classes.mainContent}`);
        if (mainContentElement) {
          mainContentElement.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }
    };

    // Start checking for content immediately
    requestAnimationFrame(handleHighlighting);
  };

  // Highlight search terms in content and scroll to specific match
  const highlightTextInContent = (term, targetSnippet = '') => {
    if (!contentRef.current || !term) return;

    // Remove existing highlights
    const existingHighlights = contentRef.current.querySelectorAll('.search-highlight');
    existingHighlights.forEach(el => {
      const parent = el.parentNode;
      parent.replaceChild(document.createTextNode(el.textContent), el);
      parent.normalize();
    });

    // Add new highlights
    const walker = document.createTreeWalker(
      contentRef.current,
      NodeFilter.SHOW_TEXT,
      null,
      false
    );

    const textNodes = [];
    let node;
    while (node = walker.nextNode()) {
      textNodes.push(node);
    }

    let targetHighlight = null;
    let firstHighlight = null;
    const allHighlights = [];

    textNodes.forEach(textNode => {
      const text = textNode.textContent;
      const regex = new RegExp(`(${term})`, 'gi');
      if (regex.test(text)) {
        const highlightedHTML = text.replace(regex, '<span class="search-highlight" style="background-color: #fff3cd; padding: 1px 2px; border-radius: 2px; font-weight: 600; border: 1px solid #ffc107;">$1</span>');
        const wrapper = document.createElement('div');
        wrapper.innerHTML = highlightedHTML;
        const fragment = document.createDocumentFragment();
        while (wrapper.firstChild) {
          fragment.appendChild(wrapper.firstChild);
        }
        textNode.parentNode.replaceChild(fragment, textNode);
      }
    });

    // Use requestAnimationFrame to find highlights after they're created
    requestAnimationFrame(() => {
      const highlights = contentRef.current.querySelectorAll('.search-highlight');
      if (highlights.length === 0) return;

      let targetElement = highlights[0]; // Default to first

      // If we have a target snippet, try to find the best match
      if (targetSnippet) {
        // Clean the target snippet for comparison
        const cleanTarget = targetSnippet
          .replace(/\.\.\./g, '') // Remove ellipsis
          .replace(/\s+/g, ' ') // Normalize whitespace
          .trim()
          .toLowerCase();

        let bestMatch = null;
        let bestScore = 0;

        highlights.forEach(highlight => {
          // Get context around this highlight (about 100 characters)
          const element = highlight;
          let contextText = '';

          // Try to get surrounding context
          const parent = element.parentNode;
          if (parent) {
            contextText = parent.textContent
              .replace(/\s+/g, ' ')
              .trim()
              .toLowerCase();

            // Calculate similarity score with target snippet
            const words = cleanTarget.split(' ').filter(w => w.length > 2);
            let score = 0;

            words.forEach(word => {
              if (contextText.includes(word)) {
                score += word.length; // Longer words get more weight
              }
            });

            if (score > bestScore) {
              bestScore = score;
              bestMatch = highlight;
            }
          }
        });

        if (bestMatch && bestScore > 0) {
          targetElement = bestMatch;
        }
      }

      scrollToElement(targetElement);
    });
  };

  // Smooth scroll to element with offset for header
  const scrollToElement = (element) => {
    if (!element) {
      console.log('❌ scrollToElement: No element provided');
      return;
    }

    console.log('🎯 scrollToElement called with element:', element);

    const mainContentElement = document.querySelector(`.${classes.mainContent}`);
    if (!mainContentElement) {
      console.log('❌ scrollToElement: Main content element not found, classes:', classes.mainContent);
      // Try alternative selector
      const altContentElement = document.querySelector('.MuiContainer-root');
      if (altContentElement) {
        console.log('✅ Found alternative content element:', altContentElement);
      }
      return;
    }

    console.log('✅ Found main content element:', mainContentElement);

    // Get element position relative to the scrollable container
    const containerRect = mainContentElement.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    console.log('Container rect:', containerRect);
    console.log('Element rect:', elementRect);

    // Calculate the relative position
    const relativeTop = elementRect.top - containerRect.top + mainContentElement.scrollTop;

    // Calculate the position to center the element in the view with some offset
    const offset = 150; // Increased offset to account for toolbar
    const targetScrollTop = relativeTop - offset;

    console.log('🚀 Scrolling to element:', element, 'Target scroll top:', targetScrollTop);

    mainContentElement.scrollTo({
      top: Math.max(0, targetScrollTop), // Don't scroll above 0
      behavior: 'smooth'
    });

    console.log('✅ scrollTo called with top:', Math.max(0, targetScrollTop));

    // Add a subtle pulse animation to make the highlighted text more noticeable
    element.style.transition = 'all 0.3s ease';
    element.style.transform = 'scale(1.02)';
    element.style.boxShadow = '0 0 12px rgba(184, 134, 11, 0.6)'; // Use change color

    setTimeout(() => {
      element.style.transform = 'scale(1)';
      element.style.boxShadow = 'none';
    }, 800);
  };

  // Handle changes navigation
  const handleNavigateChange = (newIndex) => {
    console.log('Navigating to change index:', newIndex);
    setCurrentChangeIndex(newIndex);
    // Scroll to the specific change
    scrollToChange(newIndex);
  };

  const handleToggleChanges = (visible) => {
    setChangesVisible(visible);
    // Update DOM elements visibility
    requestAnimationFrame(() => {
      const highlights = document.querySelectorAll('.change-highlight');
      highlights.forEach(highlight => {
        if (visible) {
          highlight.classList.remove('hidden');
        } else {
          highlight.classList.add('hidden');
        }
      });
    });
  };

  const scrollToChange = (changeIndex) => {
    const documentChanges = getDocumentChanges(activeItem);
    if (!documentChanges || changeIndex >= documentChanges.length) {
      console.log('❌ Invalid changeIndex or no document changes');
      return false;
    }

    const targetChange = documentChanges[changeIndex];
    console.log('🎯 Target change:', targetChange);

    // Try multiple strategies to find the element
    let highlight = null;

    // Strategy 1: Find by exact change ID
    if (targetChange?.id) {
      highlight = document.querySelector(`[data-change-id="${targetChange.id}"]`);
      console.log(`Strategy 1 - By ID "${targetChange.id}":`, highlight);
    }

    // Strategy 2: Find by change ID with variants
    if (!highlight && targetChange?.id) {
      highlight = document.querySelector(`[data-change-id="${targetChange.id}-nested"]`);
      console.log(`Strategy 2 - By nested ID "${targetChange.id}-nested":`, highlight);
    }

    // Strategy 3: Find by data-change-index
    if (!highlight) {
      highlight = document.querySelector(`[data-change-index="${changeIndex}"]`);
      console.log(`Strategy 3 - By index "${changeIndex}":`, highlight);
    }

    // Strategy 4: Find elements with .added class (for race field)
    if (!highlight && targetChange?.content === 'race field') {
      console.log(`🔍 Strategy 4 - Looking for race field with .added class`);
      const addedElements = document.querySelectorAll('table .added');
      console.log(`Strategy 4 - By .added class. Found ${addedElements.length} elements:`, addedElements);

      if (addedElements.length > 0) {
        // Find the table row containing the .added element
        highlight = addedElements[0].closest('tr');
        console.log(`Strategy 4 - Found table row with .added:`, highlight);
        console.log(`Strategy 4 - Table row details:`, {
          tagName: highlight?.tagName,
          className: highlight?.className,
          getBoundingClientRect: highlight?.getBoundingClientRect()
        });
      } else {
        console.log(`❌ Strategy 4 - No .added elements found in tables`);
        // Let's check what we DO have
        const allAdded = document.querySelectorAll('.added');
        console.log(`Found ${allAdded.length} .added elements anywhere:`, allAdded);
      }
    }

    // Strategy 5: Position-based fallback
    if (!highlight) {
      const allHighlights = Array.from(document.querySelectorAll('.change-highlight'));
      console.log(`Strategy 5 - Position-based. Found ${allHighlights.length} highlights`);

      if (allHighlights.length > 0) {
        allHighlights.sort((a, b) => {
          const rectA = a.getBoundingClientRect();
          const rectB = b.getBoundingClientRect();
          return rectA.top - rectB.top;
        });
        highlight = allHighlights[changeIndex];
        console.log(`Strategy 5 result:`, highlight);
      }
    }

    if (highlight) {
      console.log('✅ Found highlight element, scrolling to it');
      // Remove active class from all highlights and table rows
      document.querySelectorAll('.change-highlight, tr.row-added, tr.row-modified').forEach(h => h.classList.remove('active'));
      // Add active class to current highlight
      highlight.classList.add('active');

      // Scroll to element
      scrollToElement(highlight);

      // Remove active class after animation
      setTimeout(() => {
        highlight.classList.remove('active');
      }, 1000);
      return true; // Success
    } else {
      console.log('❌ Failed to find highlight');
      console.log('Available elements:');
      console.log('- .change-highlight:', document.querySelectorAll('.change-highlight').length);
      console.log('- [data-change-id]:', document.querySelectorAll('[data-change-id]').length);
      console.log('- [data-change-index]:', document.querySelectorAll('[data-change-index]').length);
      return false;
    }
  };

  const isItemAvailable = (item) => {
    if (item.children) {
      return item.children.some(child => isItemAvailable(child));
    }
    return item.content && (item.content.markdown || item.content.component);
  };

  // Helper function to check if any descendant is active
  const hasActiveDescendant = (item) => {
    if (!item.children) return false;

    for (const child of item.children) {
      if (child.id === activeItem) return true;
      if (hasActiveDescendant(child)) return true;
    }
    return false;
  };

  // Helper function to check if item or any of its children have changes
  const hasChangesInChildren = (item) => {
    if (!item.children) {
      return hasDocumentChanges(item.id);
    }

    // Check if any child has changes
    return item.children.some(child =>
      hasDocumentChanges(child.id) || hasChangesInChildren(child)
    );
  };

  const renderTreeItem = (item, level = 0, isLastInGroup = false) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const isActive = activeItem === item.id;
    const hasActiveChild = hasActiveDescendant(item);
    const isAvailable = isItemAvailable(item);
    const hasDirectChanges = hasDocumentChanges(item.id);
    const hasChildrenChanges = hasChangesInChildren(item);
    const showChangeIndicator = hasDirectChanges || hasChildrenChanges;

    const itemClass = level === 0 ? classes.treeItem :
      level === 1 ? classes.nestedItem :
        classes.doubleNestedItem;

    const finalClass = `${itemClass} ${isActive ? classes.activeItem : ''} ${hasActiveChild && !isActive ? classes.activeParent : ''} ${!isAvailable ? classes.disabledItem : ''}`;

    return (
      <Fragment key={item.id}>
        <ListItem
          button={isAvailable}
          className={finalClass}
          onClick={() => {
            if (!isAvailable) return;
            if (hasChildren) {
              handleExpandClick(item.id);
            } else {
              handleItemClick(item.id);
            }
          }}
        >
          <ListItemIcon>
            {item.icon || (hasChildren ?
                (isExpanded ? <FolderOpen style={{ fontSize: level === 0 ? '16px' : '14px' }} /> : <Folder style={{ fontSize: level === 0 ? '16px' : '14px' }} />) :
                <Description style={{ fontSize: level === 0 ? '14px' : level === 1 ? '13px' : '12px' }} />
            )}
          </ListItemIcon>
          <ListItemText primary={item.title} />
          {showChangeIndicator && (
            <div
              className={`${classes.changeIndicator} pulse ${hasDirectChanges ? '' : 'parent-changes'}`}
              title={hasDirectChanges ? "Has changes in current release" : "Has changes in child documents"}
            />
          )}
          {hasChildren && (isExpanded ?
            <ExpandLess style={{ fontSize: level === 0 ? '16px' : '14px', color: level === 0 ? '#6b7280' : '#9ca3af' }} /> :
            <ExpandMore style={{ fontSize: level === 0 ? '16px' : '14px', color: level === 0 ? '#6b7280' : '#9ca3af' }} />
          )}
        </ListItem>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child, index) =>
                renderTreeItem(child, level + 1, index === item.children.length - 1)
              )}
            </List>
          </Collapse>
        )}

        {level === 0 && !isLastInGroup && (
          <div className={classes.sectionDivider} />
        )}

        {/* Add special divider after users section */}
        {level === 0 && item.id === 'users' && (
          <>
            <div className={classes.sectionDivider} style={{ margin: '32px 66px', backgroundColor: '#cbd5e1', height: '2px' }} />
            <div className={classes.sectionDivider} />
          </>
        )}
      </Fragment>
    );
  };

  const findActiveContent = (items) => {
    for (const item of items) {
      if (item.id === activeItem && item.content) {
        return item.content;
      }
      if (item.children) {
        const found = findActiveContent(item.children);
        if (found) return found;
      }
    }
    return null;
  };

  const activeContent = findActiveContent(documentationStructure);

  const filteredStructure = searchTerm ?
    documentationStructure.filter(item =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.children && item.children.some(child =>
        child.title.toLowerCase().includes(searchTerm.toLowerCase()),
      )),
    ) : documentationStructure;

  // Process markdown content with change highlights
  const processMarkdownWithChanges = (markdown, documentId) => {
    if (!markdown) return '';

    const documentChanges = getDocumentChanges(documentId);

    if (!documentChanges || documentChanges.length === 0) {
      return marked(markdown);
    }

    console.log('Processing changes for document:', documentId, 'Changes:', documentChanges);

    let processedMarkdown = marked(markdown);
    console.log('Original markdown processed:', processedMarkdown.substring(0, 200) + '...');

    // Debug: Look for preferredPhone in the HTML
    if (processedMarkdown.includes('preferredPhone')) {
      console.log('Found preferredPhone in HTML - showing context:');
      const phoneIndex = processedMarkdown.indexOf('preferredPhone');
      console.log(processedMarkdown.substring(phoneIndex - 100, phoneIndex + 200));
    }

    // For demo purposes, let's create guaranteed highlights in specific locations
    documentChanges.forEach((change, index) => {
      const highlightDiv = `<div class="change-highlight ${change.type}" data-change-id="${change.id}" data-change-index="${index}">
        <div class="change-tooltip">${change.title} - ${change.description}</div>
        $1
      </div>`;

      console.log(`Processing change ${index}:`, change.content);
      let applied = false;

      // Try exact content match first
      if (change.content && change.content !== 'race field') {
        const contentRegex = new RegExp(`(${change.content.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const beforeReplace = processedMarkdown;
        processedMarkdown = processedMarkdown.replace(contentRegex, highlightDiv);

        if (beforeReplace !== processedMarkdown) {
          console.log(`✅ Applied exact match for change ${index}: "${change.content}"`);
          applied = true;
        }
      } else if (change.content === 'race field') {
        // Skip exact content match for race field - use existing HTML highlighting
        console.log(`⏭️ Skipping exact content match for race field`);
        applied = true;
      }

      // If exact match failed, use strategic placement
      if (!applied) {
        console.log(`❌ No exact match found for change ${index}, using strategic placement`);

        if (change.type === 'added' && change.id === 'change-1') {
          // For preferredPhone field, use a simpler approach to target table rows containing preferredPhone
          console.log('Processing preferredPhone highlighting...');

          // First try to highlight the main preferredPhone row
          const phoneRowRegex = /(<tr>[\s\S]*?<strong>preferredPhone<\/strong>[\s\S]*?<\/tr>)/gi;
          const beforeReplace1 = processedMarkdown;
          processedMarkdown = processedMarkdown.replace(phoneRowRegex, `<div class="change-highlight ${change.type}" data-change-id="${change.id}" data-change-index="${index}"><div class="change-tooltip">${change.title}</div>$1</div>`);

          if (beforeReplace1 !== processedMarkdown) {
            applied = true;
            console.log(`✅ Applied preferredPhone main row highlight for change ${index}`);
          }

          // Also try to highlight the nested preferredPhone.phone row
          const phoneNestedRegex = /(<tr>[\s\S]*?preferredPhone\.phone[\s\S]*?<\/tr>)/gi;
          const beforeReplace2 = processedMarkdown;
          processedMarkdown = processedMarkdown.replace(phoneNestedRegex, `<div class="change-highlight ${change.type}" data-change-id="${change.id}-nested" data-change-index="${index}"><div class="change-tooltip">${change.title}</div>$1</div>`);

          if (beforeReplace2 !== processedMarkdown) {
            applied = true;
            console.log(`✅ Also applied preferredPhone nested row highlight for change ${index}`);
          }

          // If neither worked, try a simpler fallback targeting any element containing preferredPhone
          if (!applied) {
            console.log(`❌ No table row matches found, trying simpler approach`);
            const simpleRegex = /(\bpreferredPhone\b)/gi;
            const matches = processedMarkdown.match(simpleRegex);
            console.log(`Found ${matches ? matches.length : 0} preferredPhone mentions`);

            if (matches && matches.length > 0) {
              // Wrap the first occurrence only to avoid duplicates
              let hasReplaced = false;
              processedMarkdown = processedMarkdown.replace(simpleRegex, (match) => {
                if (!hasReplaced) {
                  hasReplaced = true;
                  return `<div class="change-highlight ${change.type}" data-change-id="${change.id}" data-change-index="${index}"><div class="change-tooltip">${change.title}</div>${match}</div>`;
                }
                return match;
              });
              applied = true;
              console.log(`✅ Applied simple preferredPhone highlight for change ${index}`);
            }
          }
        } else if (change.type === 'modified' && change.id === 'change-2') {
          // For phone format change, target the "US phone format" text specifically
          console.log('Processing phone format modification highlighting...');

          // Try to find "US phone format" text
          const phoneFormatRegex = /(US phone format)/gi;
          const matches = processedMarkdown.match(phoneFormatRegex);
          console.log(`Found ${matches ? matches.length : 0} "US phone format" mentions`);

          if (matches && matches.length > 0) {
            // Wrap the first occurrence only
            let hasReplaced = false;
            processedMarkdown = processedMarkdown.replace(phoneFormatRegex, (match) => {
              if (!hasReplaced) {
                hasReplaced = true;
                return `<div class="change-highlight ${change.type}" data-change-id="${change.id}" data-change-index="${index}"><div class="change-tooltip">${change.title}</div>${match}</div>`;
              }
              return match;
            });
            applied = true;
            console.log(`✅ Applied US phone format highlight for change ${index}`);
          }
        } else if (change.type === 'new-section' && change.id === 'change-3') {
          // For new API section, add at the end of the document
          console.log('Processing new API section highlighting...');

          // Add a new API section at the end of the content
          const newApiSection = `
<div class="change-highlight ${change.type}" data-change-id="${change.id}" data-change-index="${index}">
  <div class="change-tooltip">${change.title}</div>
  <h2>🆕 API Integration</h2>
  <p>New endpoints have been added for enhanced patient contact management:</p>
  <ul>
    <li><strong>GET /api/patients/preferred-contact</strong> - Retrieve patient's preferred contact information</li>
    <li><strong>POST /api/patients/validate-phone</strong> - Validate phone number format before saving</li>
  </ul>
</div>`;

          processedMarkdown += newApiSection;
          applied = true;
          console.log(`✅ Applied new API section highlight for change ${index}`);
        }

        // Generic fallback - find any table cell or paragraph and force application
        if (!applied) {
          console.log(`🚨 Using generic fallback for change ${index}`);

          if (change.type === 'added' && change.content === 'race field') {
            // Skip generic fallback for race field - it already has <span class="added"> in HTML
            console.log(`⏭️ Skipping generic fallback for race field - using existing HTML highlighting`);
            applied = true;
          } else if (change.type === 'modified') {
            // Force add highlight to second table row
            const allRows = processedMarkdown.match(/<tr>\s*<td[^>]*>[^<]+<\/td>/gi);
            if (allRows && allRows.length > 1) {
              const secondRowRegex = new RegExp(allRows[1].replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
              processedMarkdown = processedMarkdown.replace(secondRowRegex, `<div class="change-highlight ${change.type}" data-change-id="${change.id}" data-change-index="${index}"><div class="change-tooltip">${change.title} - ${change.description}</div>${allRows[1]}</div>`);
              applied = true;
            }
          } else if (change.type === 'new-section') {
            // Add at the beginning of the document
            processedMarkdown = `<div class="change-highlight ${change.type}" data-change-id="${change.id}" data-change-index="${index}"><div class="change-tooltip">${change.title} - ${change.description}</div><h2>🆕 ${change.section}</h2><p>${change.description}</p></div>` + processedMarkdown;
            applied = true;
          }

          console.log(`Generic fallback applied for change ${index}:`, applied);
        }
      }

      console.log(`Change ${index} applied:`, applied);
    });

    console.log('Final processed markdown contains highlights:', processedMarkdown.includes('change-highlight'));
    return processedMarkdown;
  };

  const renderContent = (content) => {
    if (!content) return null;

    return (
      <Box ref={contentRef}>
        <Box className={classes.breadcrumbs}>
          <Typography variant="body2" color="textSecondary">
            Documentation / {content.title}
          </Typography>
        </Box>

        <Box className={classes.contentSection}>
          <Typography variant="h4" className={classes.sectionTitle}>
            {content.title}
          </Typography>
          <Typography variant="body1" className={classes.sectionDescription}>
            {content.description}
          </Typography>

          {content.tags && (
            <Box mt={2}>
              {content.tags.map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  className={classes.tag}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Changes Toolbar */}
        <ChangesToolbar
          documentId={activeItem}
          currentChangeIndex={currentChangeIndex}
          onNavigateChange={handleNavigateChange}
          onToggleChanges={handleToggleChanges}
          changesVisible={changesVisible}
        />

        {content.component ? (
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '32px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: 'linear-gradient(90deg, #334155 0%, #475569 50%, #64748b 100%)',
              borderRadius: '12px 12px 0 0'
            }} />
            <content.component onNavigateToDocument={handleItemClick} />
          </div>
        ) : content.markdown ? (
          <div
            className={classes.markdownContent}
            dangerouslySetInnerHTML={{ __html: processMarkdownWithChanges(content.markdown, activeItem) }}
          />
        ) : (
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '32px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
            textAlign: 'center',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: 'linear-gradient(90deg, #64748b 0%, #94a3b8 50%, #cbd5e1 100%)',
              borderRadius: '12px 12px 0 0'
            }} />
            <Typography variant="h6" style={{ color: '#374151', marginBottom: '12px' }}>
              Documentation Coming Soon
            </Typography>
            <Typography variant="body2" style={{ color: '#6b7280' }}>
              Detailed documentation for this section is currently being prepared.
              Please check back later or contact the development team for specific information about this feature.
            </Typography>
          </div>
        )}
      </Box>
    );
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.header} square elevation={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" className={classes.title}>
              QHS Documentation
            </Typography>
          </Box>
          <Box>
            <GlobalSearch onNavigate={handleSearchNavigation} />
          </Box>
        </Box>
      </Paper>

      <Grid container className={classes.content}>
        <Grid item className={classes.sidebar}>
          <List className={classes.navigationTree}>
            {filteredStructure.map((item, index) =>
              renderTreeItem(item, 0, index === filteredStructure.length - 1)
            )}
          </List>
        </Grid>

        <Grid item className={classes.mainContent}>
          {activeContent ? renderContent(activeContent) : (
            <Box textAlign="center" pt={8}>
              <Typography variant="h5" color="textSecondary" gutterBottom>
                Select a topic from the navigation
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Choose an item from the tree navigation to view its documentation.
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default Documentation;
